import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Brain, Calendar, RefreshCw, Plus, Trash2, X, Send, Wrench, Mail, TrendingUp, Package2, MessageCircle, Calculator, Users as UsersIcon, Sparkles, AlertTriangle, Wand2, Factory, Radar, Clock, Truck } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import BrainPanel from '../components/BrainPanel';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

const CHART_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const TABS = [
  { key: 'business',    label: 'Business Intelligence AI', icon: Brain },
  { key: 'runout',      label: 'Run-out Radar', icon: Radar },
  { key: 'inventory',   label: 'Inventory Brain', icon: Package2 },
  { key: 'production',  label: 'Production Brain', icon: Factory },
  { key: 'cashflow',    label: 'Cashflow AI', icon: TrendingUp },
  { key: 'supplier',    label: 'Supplier Alternatives AI', icon: Package2 },
  { key: 'outreach',    label: 'Client Outreach AI', icon: MessageCircle },
  { key: 'bookkeeping', label: 'Bookkeeping AI', icon: Calculator },
  { key: 'labor',       label: 'Labor / Hiring AI', icon: UsersIcon },
  { key: 'maint',       label: 'Machine Maintenance', icon: Wrench },
  { key: 'briefing',    label: 'Briefing Recipients', icon: Mail },
];

export default function IntelligenceSuite() {
  const [tab, setTab] = useState('business');
  const [askAllOpen, setAskAllOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="space-y-4" data-testid="intelligence-suite">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">AI Intelligence Suite</h1>
          <p className="text-zinc-500 text-xs mt-0.5">Power-BI visualizations · scoping questions · deep-dive LLM chat</p>
        </div>
        <button onClick={() => setAskAllOpen(true)} data-testid="ask-all-ais-btn"
          className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold text-xs px-4 py-2 rounded-xl shadow-lg">
          <Sparkles size={13} /> Ask All 6 AIs
        </button>
      </div>
      {askAllOpen && <AskAllAIsModal onClose={() => setAskAllOpen(false)} />}
      <div className="flex flex-wrap gap-2">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ai-tab-${t.key}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${
              tab === t.key ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'
            }`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'business'    && <BusinessIntelligencePanel />}
      {tab === 'runout'      && <RunoutRadarPanel navigate={navigate} />}
      {tab === 'inventory'   && <BrainPanel kind="inventory" navigate={navigate} />}
      {tab === 'production'  && <BrainPanel kind="production" navigate={navigate} />}
      {tab === 'cashflow'    && <CashflowPanel />}
      {tab === 'supplier'    && <SupplierPanel />}
      {tab === 'outreach'    && <OutreachPanel />}
      {tab === 'bookkeeping' && <BookkeepingPanel />}
      {tab === 'labor'       && <LaborPanel />}
      {tab === 'maint'       && <MaintenancePanel />}
      {tab === 'briefing'    && <BriefingPanel />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Shared AI Chat block — summary + scoping chips + Q&A
// ──────────────────────────────────────────────────────────────────────────
function ChatBlock({ endpoint, placeholder, scopingQuestions, autoInsight }) {
  const [q, setQ] = useState('');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  // Auto-insight: fire the default scoping question once on mount so the
  // user lands on a populated card without having to click anything.
  // Set `autoInsight={true}` (use the first scoping question) or pass a
  // string to specify a custom prompt.
  const autoFiredRef = React.useRef(false);
  // iter217 — Graceful budget-exceeded fallback. When the Universal LLM Key
  // runs out of credit, the backend returns a 4xx with "budget" or "credit"
  // in the detail. We surface a friendly inline card instead of a generic
  // error toast so the operator knows what to do.
  const [budgetExhausted, setBudgetExhausted] = useState(false);
  const ask = async (text) => {
    if (busy) return;
    const question = (text ?? q ?? '').trim() || placeholder;
    setBusy(true); setAnswer(''); setBudgetExhausted(false);
    try {
      const res = await axios.post(`${API}/${endpoint}/analysis`, { question }, { withCredentials: true });
      setAnswer(res.data.analysis || '');
    } catch (e) {
      const detail = (e.response?.data?.detail || e.message || '').toLowerCase();
      if (detail.includes('budget') || detail.includes('credit') || detail.includes('quota') || detail.includes('insufficient')) {
        setBudgetExhausted(true);
      } else {
        toast.error(e.response?.data?.detail || 'AI failed');
      }
    }
    finally { setBusy(false); }
  };
  React.useEffect(() => {
    if (!autoInsight || autoFiredRef.current) return;
    autoFiredRef.current = true;
    const prompt = typeof autoInsight === 'string' ? autoInsight : (scopingQuestions?.[0] || placeholder);
    if (prompt) ask(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-5" data-testid={`${endpoint}-chat`}>
      <h3 className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-amber-400" /> Ask the AI
      </h3>
      {scopingQuestions?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`${endpoint}-scoping-chips`}>
          {scopingQuestions.map((sq, i) => (
            <button key={i} onClick={() => { setQ(sq); ask(sq); }}
              className="text-[11px] px-2.5 py-1.5 rounded-full border border-zinc-700 bg-zinc-950 text-zinc-400 hover:text-amber-300 hover:border-amber-500/50 transition-all">
              {sq}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder}
          data-testid={`${endpoint}-q`}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
          className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
        <button onClick={() => ask()} disabled={busy} data-testid={`${endpoint}-ask`}
          className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black rounded-lg text-xs font-semibold">
          <Send size={12} /> {busy ? 'Thinking…' : 'Ask'}
        </button>
      </div>
      {budgetExhausted && (
        <div className="mt-4 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 flex items-start gap-3" data-testid={`${endpoint}-budget-exhausted`}>
          <AlertTriangle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-amber-200 text-sm font-semibold mb-1">
              AI summary paused — Universal LLM Key budget exhausted
            </div>
            <div className="text-amber-100/80 text-[12px] leading-relaxed">
              Top up your Universal LLM Key from the Emergent dashboard to resume AI insights.
              Go to <span className="font-mono bg-amber-950/40 px-1.5 py-0.5 rounded">Profile → Universal Key → Add Balance</span>
              {' '}(or enable auto-recharge). All non-AI features continue to work normally.
            </div>
          </div>
        </div>
      )}
      {answer && (
        <div className="mt-4 bg-zinc-950 border border-zinc-800 rounded-lg p-4 prose prose-invert prose-sm max-w-none" data-testid={`${endpoint}-answer`}>
          <ReactMarkdown>{answer}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function useDashboard(endpoint) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // iter194 — bypassCache so the page-level refresh button actually
      // hits the backend (not the 5s GET cache). Production users were
      // mashing Refresh and getting the same cached failed-snapshot.
      // 25s timeout so a slow snapshot returns an explicit error instead
      // of letting the cloudflare/ingress 30s wall serve a generic 504.
      const r = await axios.get(`${API}/${endpoint}/dashboard`, {
        withCredentials: true,
        bypassCache: true,
        timeout: 25000,
      });
      setData(r.data);
    } catch (e) {
      // iter194 — Was silently calling setLoading(false) + leaving data null,
      // which made the panel say "Loading…" FOREVER even when the call had
      // long failed. Surface the actual error so the user knows whether it's
      // auth, a server bug, or a connection issue, and gets a retry button.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      const lower = String(detail).toLowerCase();
      let friendly;
      // iter217 — Budget exhausted handled separately as a soft state.
      if (lower.includes('budget') || lower.includes('credit') || lower.includes('quota') || lower.includes('insufficient')) {
        friendly = 'BUDGET_EXHAUSTED';
      } else if (status === 401) friendly = 'Session expired — please refresh and log in again.';
      else if (status === 403) friendly = 'You do not have permission to view this AI snapshot.';
      else if (status === 504 || e?.code === 'ECONNABORTED' || (status === 503 && e?.response?.data?.error_type === 'timeout')) friendly = 'The snapshot took too long to compute. Try Refresh in a moment.';
      else if (!status) friendly = 'Failed to load snapshot: no response reached the browser (server or CORS error). Try Refresh; if it persists, share the time with support.';
      else {
        const rid = e?.response?.data?.request_id;
        friendly = `Failed to load snapshot (${status}): ${detail}${rid ? ` · ref ${rid}` : ''}`;
      }
      setError(friendly);
      if (friendly !== 'BUDGET_EXHAUSTED') toast.error(friendly);
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [endpoint]);
  return { data, loading, error, refresh: load };
}

// iter194 — Consistent "loading / error / retry" UI for every AI panel.
// Previously the panels showed "Loading…" forever when the API call
// failed silently. Now we surface the real error and a one-click Retry.
function PanelState({ loading, error, onRetry, label = 'snapshot' }) {
  if (loading) return <div className="text-zinc-500 text-sm animate-pulse p-6">Loading {label}…</div>;
  if (error === 'BUDGET_EXHAUSTED') {
    return (
      <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-4 flex items-start gap-3" data-testid="ai-panel-budget-exhausted">
        <AlertTriangle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-amber-200 text-sm font-semibold mb-1">AI summary paused — Universal LLM Key budget exhausted</div>
          <div className="text-amber-100/80 text-[12px] mb-2 leading-relaxed">
            Top up your Universal LLM Key to resume AI insights. Go to <span className="font-mono bg-amber-950/40 px-1.5 py-0.5 rounded">Profile → Universal Key → Add Balance</span> (or enable auto-recharge). All non-AI features keep working normally.
          </div>
          <button onClick={onRetry} data-testid="ai-panel-retry"
            className="text-[11px] bg-amber-700/40 hover:bg-amber-700/60 border border-amber-600/60 text-amber-100 px-3 py-1 rounded-md inline-flex items-center gap-1">
            <RefreshCw size={11} /> Try again
          </button>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-900/15 border border-red-700/50 rounded-xl p-4 flex items-start gap-3" data-testid="ai-panel-error">
        <AlertTriangle size={18} className="text-red-300 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-red-200 text-sm font-semibold mb-1">Could not load {label}</div>
          <div className="text-red-100/80 text-[11px] mb-2 break-words">{error}</div>
          <button onClick={onRetry} data-testid="ai-panel-retry"
            className="text-[11px] bg-red-700/40 hover:bg-red-700/60 border border-red-600/60 text-red-100 px-3 py-1 rounded-md inline-flex items-center gap-1">
            <RefreshCw size={11} /> Retry
          </button>
        </div>
      </div>
    );
  }
  return null;
}

function KPIRow({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((it, i) => (
        <div key={i} className={`rounded-xl border p-4 ${it.className || 'bg-zinc-900 border-zinc-800'}`} data-testid={`kpi-${it.id}`}>
          <div className="flex items-center justify-between mb-1">
            {it.icon && <it.icon size={13} className="opacity-70" />}
            {it.badge && <span className="text-[10px] opacity-70">{it.badge}</span>}
          </div>
          <div className="font-mono font-bold text-xl text-zinc-50">{it.value}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function PanelShell({ title, children, onRefresh, testid }) {
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-semibold text-zinc-50">{title}</h2>
        {onRefresh && <button onClick={onRefresh} className="p-2 rounded-lg border border-zinc-800 text-zinc-400 hover:bg-zinc-800"><RefreshCw size={13} /></button>}
      </div>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Business Intelligence AI Panel (iter212) — unified Order + Production + Forecast
// Merges the previous 2 separate panels into a single brain that learns from
// the past 6 months of order history to predict upcoming demand, surface
// production state, and flag RM gaps before they bite.
// ──────────────────────────────────────────────────────────────────────────
function BusinessIntelligencePanel() {
  const { data, loading, error, refresh } = useDashboard('business-intelligence-ai');
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;
  const sc = data.summary_counts || {};
  const cs = data.current_state || {};
  const trendRows = (data.product_velocity || []).map(p => ({
    name: p.product, q30: p.qty_30d, q90: p.qty_90d, q180: p.qty_180d,
  }));
  const trendColor = (t) => t === 'rising' ? 'text-emerald-300 bg-emerald-900/30 border-emerald-800/50'
                  : t === 'declining' ? 'text-red-300 bg-red-900/30 border-red-800/50'
                  : t === 'stable'    ? 'text-amber-300 bg-amber-900/20 border-amber-800/40'
                  : t === 'new'       ? 'text-sky-300 bg-sky-900/30 border-sky-800/50'
                  : 'text-zinc-400 bg-zinc-800 border-zinc-700';
  return (
    <PanelShell title="Business Intelligence — Patterns, Forecast & Production" onRefresh={refresh} testid="business-intel-dashboard">
      <KPIRow items={[
        { id: 'orders-30d', label: 'Orders · 30d', value: fmtNum(data.periods?.last_30d?.count || 0) + ' · ' + fmt(data.periods?.last_30d?.value || 0), icon: TrendingUp },
        { id: 'cust-due', label: 'Customers due 7d', value: fmtNum(sc.customers_due_to_order || 0), className: 'bg-emerald-900/20 border-emerald-800/40' },
        { id: 'cust-overdue', label: 'Customers overdue', value: fmtNum(sc.customers_overdue_to_order || 0), className: (sc.customers_overdue_to_order || 0) > 0 ? 'bg-red-900/20 border-red-800/50' : '' },
        { id: 'rising', label: 'Rising products', value: fmtNum(sc.rising_products || 0), icon: Sparkles, className: 'bg-emerald-900/20 border-emerald-800/40' },
        { id: 'declining', label: 'Declining products', value: fmtNum(sc.declining_products || 0), className: (sc.declining_products || 0) > 0 ? 'bg-amber-900/20 border-amber-800/40' : '' },
        { id: 'rm-gaps', label: 'RM gaps 30d', value: fmtNum(sc.rm_shortfalls_30d || 0), icon: AlertTriangle, className: (sc.rm_shortfalls_30d || 0) > 0 ? 'bg-red-900/20 border-red-800/50' : '' },
      ]} />

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Customer ordering patterns */}
        <ChartCard title={`Customer Patterns · Predicted Next Orders (top ${(data.customer_patterns || []).length})`}>
          <div className="text-[11px] space-y-1 max-h-72 overflow-y-auto pr-1" data-testid="customer-patterns-list">
            {(data.customer_patterns || []).map((c, i) => (
              <div key={i} className={`flex items-start justify-between gap-2 px-2 py-1.5 rounded-md border ${c.is_overdue ? 'bg-red-900/10 border-red-800/40' : 'bg-zinc-900/40 border-zinc-800/60'}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 font-medium truncate">{c.client}</div>
                  <div className="text-zinc-500 text-[10px] mt-0.5">
                    Cadence ~{c.avg_gap_days}d · last {c.days_since_last}d ago · top: {c.top_products.slice(0,2).map(p => p.name).join(', ') || '—'}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[10px] font-semibold ${c.is_overdue ? 'text-red-300' : c.days_to_next <= 7 ? 'text-emerald-300' : 'text-zinc-400'}`}>
                    {c.is_overdue ? `${Math.abs(c.days_to_next)}d overdue` : c.days_to_next === 0 ? 'today' : `in ${c.days_to_next}d`}
                  </div>
                  <div className="text-zinc-500 text-[10px] font-mono">{c.predicted_next_date}</div>
                  <div className="text-amber-400 text-[10px] font-mono">avg {fmt(c.avg_order_value)}</div>
                </div>
              </div>
            ))}
            {(data.customer_patterns || []).length === 0 && <div className="text-zinc-500 py-4 text-center">Not enough order history yet (need ≥2 orders / customer)</div>}
          </div>
        </ChartCard>

        {/* Product velocity */}
        <ChartCard title="Product Velocity · 30 / 90 / 180-day windows">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trendRows.slice(0, 8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis type="number" stroke="#71717a" fontSize={10} />
              <YAxis type="category" dataKey="name" stroke="#71717a" fontSize={10} width={100} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="q30" name="30d" fill="#10b981" />
              <Bar dataKey="q90" name="90d" fill="#3b82f6" />
              <Bar dataKey="q180" name="180d" fill="#71717a" />
            </BarChart>
          </ResponsiveContainer>
          <div className="text-[10px] space-y-1 max-h-32 overflow-y-auto pr-1 mt-2 border-t border-zinc-800 pt-2">
            {(data.product_velocity || []).slice(0, 10).map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <span className="text-zinc-300 truncate flex-1">{p.product}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border ${trendColor(p.trend)}`}>
                  {p.trend} {p.trend !== 'dormant' && p.trend !== 'new' ? `${p.trend_pct > 0 ? '+' : ''}${p.trend_pct}%` : ''}
                </span>
                <span className="text-amber-400/80 text-[10px] font-mono w-16 text-right">~{p.projected_30d_qty}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* RM forecast gaps */}
      <ChartCard title={`Projected RM Shortfall (next 30d, recipe × forecast vs stock + inbound) — ${(data.rm_forecast_gaps || []).length}`}>
        <div className="text-[11px] space-y-1 max-h-56 overflow-y-auto pr-1" data-testid="rm-forecast-gaps">
          {(data.rm_forecast_gaps || []).length === 0
            ? <div className="text-emerald-400 py-3 text-center">✓ RM cover holds for projected demand</div>
            : (data.rm_forecast_gaps || []).map((g, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5 bg-red-900/10 border border-red-800/40 rounded-md">
                <div className="flex-1">
                  <div className="text-zinc-200 font-medium">{g.material}</div>
                  <div className="text-zinc-500 text-[10px] mt-0.5">
                    Need {g.projected_need_30d} {g.unit} · have {g.current_stock} · inbound {g.inbound_po} · lead {g.lead_time_days}d
                  </div>
                </div>
                <div className="text-red-300 font-mono text-[12px] font-semibold whitespace-nowrap">
                  − {g.shortfall} {g.unit}
                </div>
              </div>
            ))}
        </div>
      </ChartCard>

      {/* Current production snapshot */}
      <ChartCard title="Current Production State">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-md p-2">
            <div className="text-zinc-500">Open orders</div>
            <div className="text-zinc-200 font-mono text-lg">{cs.open_orders || 0}</div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-md p-2">
            <div className="text-zinc-500">Active batches</div>
            <div className="text-zinc-200 font-mono text-lg">{cs.active_batches || 0}</div>
          </div>
          <div className={`border rounded-md p-2 ${(cs.overdue_dispatches || 0) > 0 ? 'bg-red-900/20 border-red-800/50' : 'bg-zinc-900/60 border-zinc-800'}`}>
            <div className="text-zinc-500">Overdue dispatches</div>
            <div className={`font-mono text-lg ${(cs.overdue_dispatches || 0) > 0 ? 'text-red-300' : 'text-zinc-200'}`}>{cs.overdue_dispatches || 0}</div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-md p-2">
            <div className="text-zinc-500">Lines in use</div>
            <div className="text-zinc-200 font-mono text-lg">{Object.keys(cs.lines_in_use || {}).length}</div>
          </div>
        </div>
      </ChartCard>

      <ChatBlock endpoint="business-intelligence-ai" placeholder="e.g. Which customer should I follow up with this week?"
        autoInsight
        scopingQuestions={[
          "Which customers are likely to place an order this week and what should I expect them to buy?",
          "Which products should I ramp up production for given the 30-day trend?",
          "Are any raw materials at risk of stockout against projected demand?",
          "How are we doing this month compared to last month?",
          "Which products are declining and should I reach out to those customers?",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Order Management AI Panel
// ──────────────────────────────────────────────────────────────────────────
function OrderMgmtPanel() {
  const { data, loading, error, refresh } = useDashboard('order-mgmt-ai');
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;
  const pipelineBars = Object.entries(data.pipeline || {}).map(([name, value]) => ({ name, value }));
  const prioPie = Object.entries(data.priority_distribution || {}).map(([name, value]) => ({ name, value }));
  return (
    <PanelShell title="Order Management — Live Snapshot" onRefresh={refresh} testid="order-mgmt-ai-dashboard">
      <KPIRow items={[
        { id: 'open-orders', label: 'Open orders', value: fmtNum(data.total_open_orders), icon: Brain },
        { id: 'overdue', label: 'Overdue dispatches', value: fmtNum((data.overdue || []).length), icon: AlertTriangle, className: 'bg-red-900/20 border-red-800/50' },
        { id: 'active-batches', label: 'Active batches', value: fmtNum(data.total_active_batches) },
        { id: 'shortages', label: 'Material shortages', value: fmtNum((data.material_bottlenecks || []).length), className: 'bg-amber-900/20 border-amber-800/50' },
      ]} />
      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Pipeline Buckets">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={pipelineBars}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" stroke="#71717a" fontSize={10} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46', borderRadius: 8 }} />
              <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Priority Distribution">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={prioPie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} label>
                {prioPie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      {data.material_bottlenecks?.length > 0 && (
        <ChartCard title={`Top Material Bottlenecks (${data.material_bottlenecks.length})`}>
          <div className="text-[11px] space-y-1">
            {data.material_bottlenecks.slice(0, 8).map((m, i) => (
              <div key={i} className="flex justify-between border-b border-zinc-800/50 py-1">
                <span className="text-zinc-300">{m.material}</span>
                <span className="text-red-300">short by {m.short_by} {m.unit}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      )}
      <ChatBlock endpoint="order-mgmt-ai" placeholder="e.g. What's the 7-day schedule? Which orders are at risk?"
        autoInsight
        scopingQuestions={[
          "Give me a 7-day production & dispatch schedule.",
          "Which open orders are at highest dispatch risk?",
          "What material shortages block my top 5 priority orders?",
          "Which production line is over-loaded this week?",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Production Planning AI Panel
// ──────────────────────────────────────────────────────────────────────────
function ProductionPlanningPanel() {
  const { data, loading, error, refresh } = useDashboard('production-planning-ai');
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;
  const lineRows = Object.entries(data.lines || {}).map(([name, d]) => ({ name, orders: d.order_count, qty: d.total_qty }));
  return (
    <PanelShell title="Production Planning — Live Snapshot" onRefresh={refresh} testid="production-planning-ai-dashboard">
      <KPIRow items={[
        { id: 'lines', label: 'Active lines', value: fmtNum(Object.keys(data.lines || {}).length) },
        { id: 'total-orders', label: 'Queued orders', value: fmtNum(lineRows.reduce((s, r) => s + r.orders, 0)) },
        { id: 'maint', label: 'Maintenance windows', value: fmtNum((data.maintenance || []).length), className: 'bg-amber-900/20 border-amber-800/50' },
        { id: 'near-expiry', label: 'Near-expiry lots (≤45d)', value: fmtNum((data.near_expiry_lots || []).length), className: 'bg-red-900/20 border-red-800/50' },
      ]} />
      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Line Load (orders queued)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={lineRows} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis type="number" stroke="#71717a" fontSize={10} />
              <YAxis type="category" dataKey="name" stroke="#71717a" fontSize={10} width={90} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} />
              <Bar dataKey="orders" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Near-Expiry Lots (≤45 days)">
          <div className="text-[11px] space-y-1 max-h-56 overflow-y-auto pr-1">
            {(data.near_expiry_lots || []).slice(0, 10).map((l, i) => (
              <div key={i} className="flex justify-between border-b border-zinc-800/50 py-1">
                <span className="text-zinc-300 truncate">{l.material} · {l.lot_no}</span>
                <span className={l.days_left < 15 ? 'text-red-300' : 'text-amber-300'}>{l.days_left}d · {l.qty}</span>
              </div>
            ))}
            {(data.near_expiry_lots || []).length === 0 && <div className="text-zinc-500 py-4">No lots near expiry.</div>}
          </div>
        </ChartCard>
      </div>
      <ChatBlock endpoint="production-planning-ai" placeholder="e.g. Plan this week respecting maintenance and near-expiry lots."
        scopingQuestions={[
          "Plan this week line-by-line using FIFO and maintenance windows.",
          "Which lots should I consume first to prevent waste?",
          "Which line should I add a second shift on?",
          "If Line B is down, how do I reshuffle?",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Cashflow AI Panel
// ──────────────────────────────────────────────────────────────────────────
function CashflowPanel() {
  const { data, loading, error, refresh } = useDashboard('cashflow-ai');
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;
  const bucketRows = [
    { name: '0–30d', ...data.buckets['0_30'] },
    { name: '31–60d', ...data.buckets['31_60'] },
    { name: '61–90d', ...data.buckets['61_90'] },
  ];
  return (
    <PanelShell title="Cashflow — 30/60/90 Day Forecast" onRefresh={refresh} testid="cashflow-ai-dashboard">
      <KPIRow items={[
        { id: 'ar', label: 'Open receivables', value: fmt(data.open_receivables), icon: TrendingUp },
        { id: 'ap', label: 'Open payables', value: fmt(data.open_payables), className: 'bg-orange-900/20 border-orange-800/50' },
        { id: 'overdue-ar', label: 'Overdue AR', value: fmt(data.overdue_receivables), className: data.overdue_receivables > 0 ? 'bg-red-900/20 border-red-800/50' : '' },
        { id: 'dry-spells', label: 'Dry spells projected', value: fmtNum((data.dry_spells || []).length), icon: AlertTriangle, className: (data.dry_spells || []).length > 0 ? 'bg-red-900/20 border-red-800/50' : '' },
      ]} />
      <ChartCard title="Inflow vs Outflow vs Net by Bucket">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={bucketRows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
            <YAxis stroke="#71717a" fontSize={10} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} formatter={(v) => fmt(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="inflow" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="outflow" fill="#ef4444" radius={[4, 4, 0, 0]} />
            <Bar dataKey="net" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      {data.dry_spells?.length > 0 && (
        <div className="bg-red-900/15 border border-red-700/50 rounded-xl p-4">
          <div className="text-red-300 text-sm font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Projected Dry Spells</div>
          {data.dry_spells.map((d, i) => (
            <div key={i} className="text-[11px] text-zinc-300 border-b border-red-800/30 py-1">
              {d.start} → {d.end} · min balance <span className="font-mono text-red-300">{fmt(d.min)}</span> · {d.days} days
            </div>
          ))}
        </div>
      )}
      <ChatBlock endpoint="cashflow-ai" placeholder="e.g. What does the next 90 days look like?"
        autoInsight
        scopingQuestions={[
          "Summarise the next 90 days — am I in trouble?",
          "How do I avoid the projected dry spell?",
          "Which clients should I chase today to fix this?",
          "Should I defer any vendor payments?",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Supplier Alternatives AI Panel
// ──────────────────────────────────────────────────────────────────────────
function SupplierPanel() {
  const { data, loading, error, refresh } = useDashboard('supplier-alternatives-ai');
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;
  const altRows = Object.entries(data.alternatives_by_material || {}).slice(0, 8).map(([mat, vs]) => ({ name: mat, vendors: vs.length }));
  return (
    <PanelShell title="Supplier Alternatives — Live Snapshot" onRefresh={refresh} testid="supplier-alternatives-ai-dashboard">
      <KPIRow items={[
        { id: 'delayed', label: 'Delayed POs', value: fmtNum(data.total_delayed_count), className: data.total_delayed_count > 0 ? 'bg-red-900/20 border-red-800/50' : '' },
        { id: 'delayed-val', label: 'Delayed value', value: fmt(data.total_delayed_value) },
        { id: 'mat-with-alts', label: 'Materials with alternates', value: fmtNum(Object.keys(data.alternatives_by_material || {}).length) },
        { id: 'single-src', label: 'Single-source risk', value: fmtNum(Object.values(data.alternatives_by_material || {}).filter(v => v.length <= 1).length), className: 'bg-amber-900/20 border-amber-800/50' },
      ]} />
      <ChartCard title="Alternate Vendor Count per Material">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={altRows} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis type="number" stroke="#71717a" fontSize={10} />
            <YAxis type="category" dataKey="name" stroke="#71717a" fontSize={10} width={120} />
            <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} />
            <Bar dataKey="vendors" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      {data.delayed_pos?.length > 0 && (
        <ChartCard title={`Delayed POs (${data.delayed_pos.length})`}>
          <div className="text-[11px] space-y-1">
            {data.delayed_pos.slice(0, 10).map((d, i) => (
              <div key={i} className="flex justify-between border-b border-zinc-800/50 py-1">
                <span className="text-zinc-300">{d.vendor} · {d.po_id}</span>
                <span className="text-red-300">{d.days_late}d late · {fmt(d.total_amount)}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      )}
      <ChatBlock endpoint="supplier-alternatives-ai" placeholder="e.g. Who should I call today to unblock delayed POs?"
        scopingQuestions={[
          "Suggest 3 alternate vendors for my most delayed PO.",
          "Which materials have no alternative supplier (risk)?",
          "Compare rates for my current vendors vs alternates.",
          "Draft a call script for the top delayed vendor.",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Client Outreach AI Panel
// ──────────────────────────────────────────────────────────────────────────
function OutreachPanel() {
  const { data, loading, error, refresh } = useDashboard('client-outreach-ai');
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;
  // bucket inactive clients by days-since
  const buckets = { '30-60d': 0, '60-90d': 0, '90-180d': 0, '180d+': 0 };
  (data.inactive_clients || []).forEach(c => {
    const d = c.days_since;
    if (d < 60) buckets['30-60d']++;
    else if (d < 90) buckets['60-90d']++;
    else if (d < 180) buckets['90-180d']++;
    else buckets['180d+']++;
  });
  const bucketRows = Object.entries(buckets).map(([name, value]) => ({ name, value }));
  return (
    <PanelShell title="Client Outreach — Re-engagement Candidates" onRefresh={refresh} testid="client-outreach-ai-dashboard">
      <KPIRow items={[
        { id: 'inactive', label: 'Inactive clients', value: fmtNum(data.total_inactive) },
        { id: 'overdue-reorder', label: 'Overdue for reorder', value: fmtNum(data.overdue_for_reorder), className: 'bg-amber-900/20 border-amber-800/50' },
        { id: 'top-lv', label: 'Top LTV lost', value: fmt((data.inactive_clients?.[0]?.lifetime_value) || 0) },
        { id: 'outstanding', label: 'Outstanding from lapsed', value: fmt((data.inactive_clients || []).reduce((s, c) => s + c.outstanding, 0)) },
      ]} />
      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Inactivity Duration">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={bucketRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" stroke="#71717a" fontSize={10} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} />
              <Bar dataKey="value" fill="#ec4899" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Top 5 Re-engagement Targets">
          <div className="text-[11px] space-y-1">
            {(data.inactive_clients || []).slice(0, 5).map((c, i) => (
              <div key={i} className="border-b border-zinc-800/50 py-1">
                <div className="flex justify-between">
                  <span className="text-zinc-200 font-semibold">{c.client}</span>
                  <span className="text-amber-300">{fmt(c.lifetime_value)}</span>
                </div>
                <div className="text-zinc-500 text-[10px]">{c.days_since}d silent · cadence ~{c.avg_gap_days || '?'}d {c.overdue_for_reorder ? '· 🔥 overdue' : ''}</div>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
      <ChatBlock endpoint="client-outreach-ai" placeholder="e.g. Draft WhatsApp + email for my top 5 inactive clients."
        scopingQuestions={[
          "Draft WhatsApp + Email for my top 5 inactive clients.",
          "Which client has the highest re-engagement ROI?",
          "Suggest a festive outreach for Diwali season.",
          "Draft a polite nudge for clients with outstanding dues.",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Bookkeeping AI Panel  (+ Auto-fix button)
// ──────────────────────────────────────────────────────────────────────────
function BookkeepingPanel() {
  const { data, loading, error, refresh } = useDashboard('bookkeeping-ai');
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;

  const catPie = Object.entries(data.expense_categories_mtd || {}).map(([name, v]) => ({ name, value: v.amount }));
  const gstTrend = (data.gst?.trend_3m || []).map(m => ({ name: m.month.split(' ')[0], output: m.output_gst, input: m.input_gst, payable: m.payable }));
  const uncategorized = (data.uncategorized_bills || []).length;

  const runAutoFix = async () => {
    if (uncategorized === 0) { toast.info('No uncategorized bills to fix.'); return; }
    if (!window.confirm(`Auto-categorize ${uncategorized} uncategorized bill${uncategorized > 1 ? 's' : ''} using heuristic rules?`)) return;
    setFixing(true);
    try {
      const res = await axios.post(`${API}/bookkeeping-ai/auto-fix`, {}, { withCredentials: true });
      setFixResult(res.data);
      toast.success(`Fixed ${res.data.fixed_count} bill${res.data.fixed_count !== 1 ? 's' : ''} · ${res.data.unchanged_uncategorized_count} still uncategorized`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-fix failed'); }
    finally { setFixing(false); }
  };

  return (
    <PanelShell title="Bookkeeping & Accounts — Month-End Close" onRefresh={refresh} testid="bookkeeping-ai-dashboard">
      <KPIRow items={[
        { id: 'gst-payable', label: 'GST Payable (MTD)', value: fmt(data.gst?.mtd?.payable || 0) },
        { id: 'uncat', label: 'Uncategorized bills', value: fmtNum(uncategorized), className: uncategorized > 0 ? 'bg-amber-900/20 border-amber-800/50' : '' },
        { id: 'unpaid-inv', label: 'Unpaid invoices', value: fmtNum((data.unpaid_invoices_top || []).length) },
        { id: 'missing-gstin', label: 'Missing GSTIN', value: fmtNum(data.missing_gstin_count), className: data.missing_gstin_count > 0 ? 'bg-red-900/20 border-red-800/50' : '' },
      ]} />

      {uncategorized > 0 && (
        <div className="bg-amber-900/15 border border-amber-700/50 rounded-xl p-4 flex items-center justify-between" data-testid="auto-fix-banner">
          <div>
            <div className="text-amber-300 font-semibold text-sm flex items-center gap-1.5"><Wand2 size={13} /> Auto-fix available</div>
            <div className="text-zinc-400 text-[11px] mt-0.5">Bulk-apply heuristic categories to {uncategorized} uncategorized bill{uncategorized > 1 ? 's' : ''}. Full audit log kept.</div>
          </div>
          <button onClick={runAutoFix} disabled={fixing} data-testid="bookkeeping-auto-fix-btn"
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-lg disabled:opacity-50">
            {fixing ? <RefreshCw size={12} className="animate-spin" /> : <Wand2 size={12} />} Auto-fix {uncategorized}
          </button>
        </div>
      )}

      {fixResult && (
        <div className="bg-green-900/15 border border-green-700/50 rounded-xl p-4" data-testid="auto-fix-result">
          <div className="text-green-300 font-semibold text-sm">Auto-fix complete</div>
          <div className="text-zinc-400 text-[11px] mt-1 mb-2">{fixResult.fixed_count} categorized · {fixResult.unchanged_uncategorized_count} still uncategorized · audit id <code className="text-amber-300">{fixResult.audit_log_id}</code></div>
          {fixResult.changes?.length > 0 && (
            <div className="bg-zinc-950 rounded p-2 max-h-40 overflow-y-auto text-[11px] space-y-0.5">
              {fixResult.changes.slice(0, 20).map((c, i) => (
                <div key={i} className="flex justify-between border-b border-zinc-800/50 py-1">
                  <span className="text-zinc-300 truncate">{c.vendor} · {c.invoice_number || c.bill_id}</span>
                  <span className="text-green-300">{c.before} → {c.after}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Expense Categories (MTD)">
          {catPie.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={catPie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85}>
                  {catPie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-zinc-500 py-12 text-center text-sm">No expenses this month yet.</div>}
        </ChartCard>
        <ChartCard title="GST 3-Month Trend">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={gstTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" stroke="#71717a" fontSize={10} />
              <YAxis stroke="#71717a" fontSize={10} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="output" stroke="#10b981" name="Output GST" />
              <Line type="monotone" dataKey="input" stroke="#3b82f6" name="Input GST (ITC)" />
              <Line type="monotone" dataKey="payable" stroke="#f59e0b" name="Payable" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title={`Monthly Close Checklist (${data.close_checklist?.filter(c => c.status === 'open').length || 0} open)`}>
        <div className="space-y-1 text-[11px]">
          {(data.close_checklist || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2 border-b border-zinc-800/50 py-1">
              <span className={`w-3 h-3 rounded-full ${c.status === 'open' ? (c.priority === 'high' ? 'bg-red-500' : 'bg-amber-500') : 'bg-green-500'}`} />
              <span className={c.status === 'open' ? 'text-zinc-200' : 'text-zinc-500 line-through'}>{c.item}</span>
              {c.count > 0 && <span className="text-zinc-500 ml-auto">{c.count} item{c.count > 1 ? 's' : ''}</span>}
            </div>
          ))}
        </div>
      </ChartCard>

      <ChatBlock endpoint="bookkeeping-ai" placeholder="e.g. What do I need to finish before the 20th?"
        scopingQuestions={[
          "Walk me through this month's close.",
          "Any GST red flags in the 3-month trend?",
          "Which expenses jumped this month vs last?",
          "Draft a month-end handover note for my CA.",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Labor / Hiring AI Panel
// ──────────────────────────────────────────────────────────────────────────
function LaborPanel() {
  const { data, loading, error, refresh } = useDashboard('labor-ai');
  if (loading || error || !data) return <PanelState loading={loading} error={error} onRetry={refresh} />;
  const roleRows = Object.entries(data.by_role || {}).map(([name, v]) => ({ name, count: v.count, salary: v.salary_total }));
  const prodTrend = data.production_trend_3m || [];
  return (
    <PanelShell title="Labor & Hiring — Workload Signals" onRefresh={refresh} testid="labor-ai-dashboard">
      <KPIRow items={[
        { id: 'headcount', label: 'Active headcount', value: fmtNum(data.total_headcount), icon: UsersIcon },
        { id: 'payroll', label: 'Monthly payroll', value: fmt(data.monthly_salary_outgo) },
        { id: 'opw', label: 'Orders / ops worker', value: data.orders_per_ops_worker ?? '—', className: (data.orders_per_ops_worker ?? 0) > 8 ? 'bg-red-900/20 border-red-800/50' : '' },
        { id: 'signals', label: 'Hire signals', value: fmtNum((data.signals || []).length), icon: AlertTriangle, className: (data.signals || []).some(s => s.severity === 'high') ? 'bg-red-900/20 border-red-800/50' : '' },
      ]} />
      <div className="grid md:grid-cols-2 gap-4">
        <ChartCard title="Headcount by Role">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={roleRows} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis type="number" stroke="#71717a" fontSize={10} />
              <YAxis type="category" dataKey="name" stroke="#71717a" fontSize={10} width={110} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} />
              <Bar dataKey="count" fill="#06b6d4" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Production Volume (3-month)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={prodTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={10} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip contentStyle={{ background: '#09090b', border: '1px solid #3f3f46' }} />
              <Bar dataKey="batches" fill="#f59e0b" name="Batches" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      {data.signals?.length > 0 && (
        <div className="bg-amber-900/15 border border-amber-700/50 rounded-xl p-4 space-y-2" data-testid="labor-signals">
          <div className="text-amber-300 font-semibold text-sm flex items-center gap-1.5"><AlertTriangle size={13} /> Hire Signals</div>
          {data.signals.map((s, i) => (
            <div key={i} className={`text-[11px] border-l-2 pl-2 ${s.severity === 'high' ? 'border-red-500 text-red-200' : 'border-amber-500 text-amber-200'}`}>{s.message}</div>
          ))}
        </div>
      )}
      <ChatBlock endpoint="labor-ai" placeholder="e.g. Should I hire now? Which role first?"
        scopingQuestions={[
          "Should I hire now, and which role first?",
          "Suggest a salary band for a QC/Quality role in Maharashtra.",
          "Can overtime or a shift split delay hiring?",
          "What's my payroll per unit of production?",
        ]} />
    </PanelShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Small reusable chart card
// ──────────────────────────────────────────────────────────────────────────
function ChartCard({ title, children }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="text-zinc-400 text-[11px] uppercase tracking-wider mb-2 font-semibold">{title}</div>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Machine maintenance + briefing panels (unchanged) — moved from original file
// ──────────────────────────────────────────────────────────────────────────
function MaintenancePanel() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ machine: '', start_date: '', end_date: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/machine-maintenance`, { withCredentials: true }).then(r => setRows(r.data || [])).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.machine || !form.start_date || !form.end_date) { toast.error('Fill all fields'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/machine-maintenance`, form, { withCredentials: true });
      toast.success('Maintenance window added');
      setShowAdd(false); setForm({ machine: '', start_date: '', end_date: '', reason: '' });
      load();
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this maintenance window?')) return;
    try { await axios.delete(`${API}/machine-maintenance/${id}`, { withCredentials: true }); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="maintenance-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-zinc-50 text-sm">Machine / Line Maintenance Windows</h3>
        <button onClick={() => setShowAdd(true)} data-testid="add-maint-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold">
          <Plus size={12} /> Add Window
        </button>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="border-b border-zinc-800">{['Machine / Line', 'Start', 'End', 'Reason', 'Status', ''].map(h => (
          <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2 uppercase tracking-wider">{h}</th>
        ))}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No maintenance scheduled — production AI will plan without blockers.</td></tr>}
          {rows.map(r => (
            <tr key={r.id} className="border-b border-zinc-800/50" data-testid={`maint-row-${r.id}`}>
              <td className="px-3 py-2 text-zinc-100">{r.machine || r.line}</td>
              <td className="px-3 py-2 text-zinc-400 font-mono">{r.start_date}</td>
              <td className="px-3 py-2 text-zinc-400 font-mono">{r.end_date}</td>
              <td className="px-3 py-2 text-zinc-300">{r.reason || '—'}</td>
              <td className="px-3 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">{r.status || 'scheduled'}</span></td>
              <td className="px-3 py-2"><button onClick={() => remove(r.id)} data-testid={`maint-del-${r.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="maint-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">New Maintenance Window</h3>
              <button onClick={() => setShowAdd(false)} className="text-zinc-500"><X size={16} /></button>
            </div>
            <input value={form.machine} onChange={e => setForm(f => ({ ...f, machine: e.target.value }))} placeholder="Machine / Line (e.g. Line B, Filling-2)"
              data-testid="maint-machine"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                data-testid="maint-start" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                data-testid="maint-end" className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
            </div>
            <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Reason"
              data-testid="maint-reason"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-xs">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="maint-save"
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BriefingPanel() {
  const [data, setData] = useState(null);
  const [extras, setExtras] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/push/briefing-settings`, { withCredentials: true }).then(r => { setData(r.data); setExtras((r.data.extra_recipients || []).join(', ')); }).catch(() => toast.error('Failed to load'));
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const list = extras.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
      const res = await axios.put(`${API}/push/briefing-settings`, { extra_recipients: list }, { withCredentials: true });
      toast.success(`Saved — ${res.data.extra_recipients.length} extra recipient(s)`);
      load();
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/portal/admin/send-briefing`, {}, { withCredentials: true });
      toast.success(`Test sent · ${res.data.last_run?.emails_sent || 0} email(s) · CSV attached`);
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="briefing-panel">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-3">Who gets the 10 AM Daily Brief
          <a href={`${API}/briefing/preview.html`} target="_blank" rel="noreferrer" className="text-amber-400 text-xs font-normal hover:underline ml-2" data-testid="briefing-preview-link-admin">Preview ↗</a>
        </h3>
        <div className="text-zinc-400 text-xs mb-2">
          Goes automatically to all <span className="text-amber-300">admins/owners</span> + any user who enables it under
          their Notification Preferences. You can add more email addresses below.
        </div>
        {data && (
          <div className="mb-3">
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">Default recipients ({data.default_recipients.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {data.default_recipients.map(r => (
                <span key={r.email} className="text-[11px] bg-zinc-800 border border-zinc-700 rounded-full px-2.5 py-1">
                  {r.name} <span className="text-zinc-500">· {r.email}</span>
                </span>
              ))}
            </div>
          </div>
        )}
        <label className="text-zinc-500 text-[10px] uppercase tracking-wider">Extra Recipients (comma/newline separated)</label>
        <textarea rows={3} value={extras} onChange={e => setExtras(e.target.value)}
          data-testid="briefing-extras"
          placeholder="accountant@partner.com, vendor@supplier.com"
          className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm mt-1.5" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={sendTest} disabled={busy} data-testid="briefing-send-test"
            className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs font-semibold disabled:opacity-50">
            <Mail size={12} /> Send test now
          </button>
          <button onClick={save} disabled={busy} data-testid="briefing-save"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save Recipients'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Ask All 7 AIs at Once — broadcast question, side-by-side answers
// ──────────────────────────────────────────────────────────────────────────
function AskAllAIsModal({ onClose }) {
  const [question, setQuestion] = useState('What should I focus on this week?');
  const [busy, setBusy] = useState(false);
  const [responses, setResponses] = useState([]);

  const ADVISORS = [
    { name: 'Business Intelligence', endpoint: 'business-intelligence-ai' },
    { name: 'Cashflow',              endpoint: 'cashflow-ai' },
    { name: 'Supplier Alternatives', endpoint: 'supplier-alternatives-ai' },
    { name: 'Client Outreach',       endpoint: 'client-outreach-ai' },
    { name: 'Bookkeeping',           endpoint: 'bookkeeping-ai' },
    { name: 'Labor / Hiring',        endpoint: 'labor-ai' },
  ];

  const ask = async () => {
    if (!question.trim() || busy) return;
    setBusy(true);
    // Initialize placeholder cards immediately
    setResponses(ADVISORS.map(a => ({ ...a, analysis: '', loading: true })));
    // Fan out in parallel — each advisor resolves independently and updates its own card
    ADVISORS.forEach((a, idx) => {
      axios.post(`${API}/${a.endpoint}/analysis`, { question: question.trim() }, { withCredentials: true, timeout: 90000 })
        .then(res => {
          setResponses(prev => prev.map((r, i) => i === idx ? { ...r, analysis: res.data.analysis || '_(no response)_', loading: false } : r));
        })
        .catch(err => {
          setResponses(prev => prev.map((r, i) => i === idx ? { ...r, analysis: `_(error: ${err.response?.status || err.message})_`, loading: false, error: true } : r));
        });
    });
    // Release busy once all have settled
    setTimeout(() => setBusy(false), 500);
  };

  const allDone = responses.length > 0 && responses.every(r => !r.loading);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-amber-500/40 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="ask-all-ais-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-zinc-50 flex items-center gap-2">
            <Sparkles size={16} className="text-amber-400" /> Ask All 7 AIs at Once
            {responses.length > 0 && (
              <span className="text-[11px] font-normal text-zinc-500 ml-2">
                {responses.filter(r => !r.loading).length} / {responses.length} ready
              </span>
            )}
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <p className="text-zinc-400 text-xs">Your question fans out in parallel. Each advisor answers independently &mdash; you&apos;ll see cards populate as they return.</p>
        <div className="flex gap-2">
          <input value={question} onChange={e => setQuestion(e.target.value)} data-testid="ask-all-q"
            onKeyDown={e => e.key === 'Enter' && ask()}
            placeholder="e.g. What should I focus on this week?"
            className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
          <button onClick={ask} disabled={busy || !question.trim()} data-testid="ask-all-submit"
            className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold disabled:opacity-50">
            <Send size={12} /> {busy && !allDone ? 'Asking 7…' : 'Broadcast'}
          </button>
        </div>
        {responses.length > 0 && (
          <div className="grid md:grid-cols-2 gap-3" data-testid="ask-all-responses">
            {responses.map((r, i) => (
              <div key={i} className={`bg-zinc-900 border rounded-xl p-4 ${r.loading ? 'border-zinc-800 animate-pulse' : r.error ? 'border-red-700/50' : 'border-zinc-800'}`}>
                <div className="text-amber-300 font-semibold text-xs mb-2 flex items-center gap-1.5 justify-between">
                  <span className="flex items-center gap-1.5"><Brain size={12} /> {r.name}</span>
                  {r.loading && <RefreshCw size={10} className="animate-spin text-zinc-500" />}
                </div>
                {r.loading ? (
                  <div className="text-zinc-500 text-xs">Thinking…</div>
                ) : (
                  <div className="prose prose-invert prose-sm max-w-none text-[12px] prose-p:my-1 prose-ul:my-1 prose-li:my-0">
                    <ReactMarkdown>{r.analysis || '_(no response)_'}</ReactMarkdown>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



// ── Run-out Radar — autonomous raw-material depletion watch ─────────────────
function RunoutRadarPanel({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/insights/runout-alerts`, { withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error('Could not load run-out radar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-zinc-500 text-sm p-4">Scanning raw-material burn rate…</div>;
  if (!data) return <div className="text-zinc-500 text-sm p-4">No data.</div>;

  const rows = data.rows || [];
  const uColor = (u) => u === 0 ? 'text-red-300 bg-red-500/10 border-red-500/40'
    : u === 1 ? 'text-amber-300 bg-amber-500/10 border-amber-500/40'
    : 'text-zinc-300 bg-zinc-500/10 border-zinc-500/40';

  return (
    <div className="space-y-4" data-testid="runout-radar-panel">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-zinc-100 text-sm font-semibold flex items-center gap-2"><Radar size={15} className="text-amber-400" /> Run-out Radar</h2>
            <p className="text-zinc-500 text-xs mt-1">Burn rate vs stock + what's inbound. Timed off each material's vendor lead time + safety buffer, never later than ~5 days before zero.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300" data-testid="radar-order-today">{data.order_today} order today</span>
            <span className="text-xs px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-300" data-testid="radar-this-week">{data.this_week} this week</span>
            <button onClick={load} data-testid="radar-refresh" className="text-zinc-400 hover:text-zinc-200 p-1.5 rounded-lg hover:bg-zinc-800"><RefreshCw size={14} /></button>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center text-emerald-300 text-sm" data-testid="radar-all-clear">
          All materials have enough cover for their lead time. Nothing to order right now.
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-xs" data-testid="radar-table">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800 bg-zinc-900/60">
                <th className="text-left px-3 py-2.5">Material</th>
                <th className="text-right px-3 py-2.5">Net avail</th>
                <th className="text-right px-3 py-2.5">Daily use</th>
                <th className="text-right px-3 py-2.5">Cover</th>
                <th className="text-right px-3 py-2.5">Inbound</th>
                <th className="text-left px-3 py-2.5">Order by</th>
                <th className="text-right px-3 py-2.5">Suggested</th>
                <th className="text-left px-3 py-2.5">Vendor</th>
                <th className="text-left px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`radar-row-${i}`}>
                  <td className="px-3 py-2.5">
                    <div className="text-zinc-100 font-medium">{r.material}</div>
                    <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded border ${uColor(r._u)}`}>{r.urgency}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-zinc-300">{fmtNum(r.net_available)} {r.unit}</td>
                  <td className="px-3 py-2.5 text-right text-zinc-400">{fmtNum(r.daily_use)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={r.cover_days !== '—' && r.cover_days <= 5 ? 'text-red-300 font-semibold' : 'text-zinc-300'}>
                      {r.cover_days === '—' ? '—' : `${r.cover_days} d`}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-zinc-400">
                    {r.incoming ? <span title={`ETA ${r.incoming_eta}`}>{fmtNum(r.incoming)} {r.unit}</span> : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className={r._u === 0 ? 'text-red-300 font-semibold' : r._u === 1 ? 'text-amber-300' : 'text-zinc-400'}>{r.order_by}</div>
                    <div className="text-[10px] text-zinc-600">need by {r.needed_by}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right text-zinc-100 font-semibold">{fmtNum(r.suggested_qty)} {r.unit}</td>
                  <td className="px-3 py-2.5 text-zinc-400 truncate max-w-[120px]">{r.vendor}</td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => navigate && navigate('/purchases')} data-testid={`radar-raise-po-${i}`}
                      className="flex items-center gap-1 text-[11px] bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 px-2 py-1 rounded-lg whitespace-nowrap">
                      <Truck size={11} /> Raise PO
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

